import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import PageContainer from '../PageContainer/PageContainer';
import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import restaurantService from '../../services/restaurantService';
import queueService, { QueueTokenPayload } from '../../services/queueService';
import { useRealtime } from '../../contexts/RealtimeContext';
import { useAppSelector } from '../../store/hooks';
import './QueueStatus.css';

const ACTIVE_STATUSES = ['WAITING', 'CALLING', 'READY'];
const POLL_ACTIVE_MS = 10000;
const POLL_IDLE_MS = 30000;

function statusLabel(status: string): string {
  switch (status) {
    case 'WAITING':
      return 'In line';
    case 'CALLING':
      return 'Calling now';
    case 'READY':
      return 'Ready';
    case 'SEATED':
      return 'Seated';
    case 'CANCELLED':
      return 'Cancelled';
    case 'NO_SHOW':
      return 'No show';
    default:
      return status.replace(/_/g, ' ');
  }
}

function statusMessage(status: string, position?: number): string {
  switch (status) {
    case 'WAITING':
      return position && position > 0
        ? `You are #${position} in line. This page updates automatically.`
        : 'You are on the waitlist. This page updates automatically.';
    case 'CALLING':
      return 'Your token is being called — please head to the host stand now.';
    case 'READY':
      return 'Your table is ready! Please proceed to the host stand.';
    case 'SEATED':
      return 'You have been seated. Enjoy your meal!';
    case 'CANCELLED':
      return 'This token has been cancelled.';
    case 'NO_SHOW':
      return 'This token was marked as no-show.';
    default:
      return '';
  }
}

const QueueStatus: React.FC = () => {
  const { appName, publicRef } = useParams<{ appName: string; publicRef: string }>();
  const { user, isAuthenticated } = useAppSelector((state) => state.user);
  const { subscribeQueueTokenUpdates } = useRealtime();

  const [restaurantName, setRestaurantName] = useState('');
  const [token, setToken] = useState<QueueTokenPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [cancelPhone, setCancelPhone] = useState(user?.phoneNumber || '');
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const phoneFromLogin = isAuthenticated && !!user?.phoneNumber;

  const fetchStatus = useCallback(async () => {
    if (!appName || !publicRef) return;
    try {
      const res = await queueService.getStatus(appName, publicRef);
      if (res.success && res.token) {
        setToken(res.token);
        setErrorMessage('');
      } else {
        setErrorMessage(res.message || 'Token not found.');
        setToken(null);
      }
    } catch {
      setErrorMessage('Could not load token status.');
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [appName, publicRef]);

  useEffect(() => {
    if (user?.phoneNumber) {
      setCancelPhone(user.phoneNumber);
    }
  }, [user?.phoneNumber]);

  useEffect(() => {
    if (!appName) return;
    restaurantService.getRestaurant(appName).then((res) => {
      if (res.success && res.restaurant) {
        setRestaurantName(res.restaurant.restaurantName || appName);
      }
    });
  }, [appName]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const intervalMs = token && ACTIVE_STATUSES.includes(token.status)
      ? POLL_ACTIVE_MS
      : POLL_IDLE_MS;
    const interval = setInterval(fetchStatus, intervalMs);
    return () => clearInterval(interval);
  }, [fetchStatus, token?.status]);

  useEffect(() => {
    if (!publicRef) return undefined;
    return subscribeQueueTokenUpdates((payload) => {
      if (payload.publicRef && payload.publicRef !== publicRef) return;
      fetchStatus();
    });
  }, [publicRef, subscribeQueueTokenUpdates, fetchStatus]);

  const handleCancel = async () => {
    if (!appName || !publicRef) return;
    const phone = (phoneFromLogin ? user?.phoneNumber : cancelPhone)?.trim() || '';
    if (!phone) {
      setErrorMessage('Enter your phone number to cancel.');
      return;
    }
    setCancelling(true);
    setErrorMessage('');
    try {
      const res = await queueService.cancelToken(appName, publicRef, phone);
      if (res.success) {
        setShowCancelConfirm(false);
        await fetchStatus();
      } else {
        setErrorMessage(res.message || 'Could not cancel token.');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not cancel token.';
      setErrorMessage(msg);
    } finally {
      setCancelling(false);
    }
  };

  const isReady = token?.status === 'READY' || token?.status === 'CALLING';

  return (
    <div className="queue-status-page">
      <Header />
      <PageContainer className="queue-status-container">
        <Breadcrumb
          items={[
            { label: 'Home', path: '/' },
            { label: restaurantName || appName || 'Restaurant', path: `/restaurants/${appName}` },
            { label: 'Waitlist status' },
          ]}
        />

        <div className={`queue-status-card${isReady ? ' queue-status-card--highlight' : ''}`}>
          {loading ? (
            <div className="queue-status-loading">
              <i className="fa fa-spinner fa-spin" aria-hidden="true" />
              <p>Loading your token…</p>
            </div>
          ) : errorMessage && !token ? (
            <div className="queue-status-empty">
              <div className="queue-status-icon queue-status-icon--muted" aria-hidden="true">
                <i className="fa fa-clock-o" />
              </div>
              <h2>Token not found</h2>
              <p>{errorMessage}</p>
              <Link to={`/restaurants/${appName}/join-queue`} className="queue-status-btn">
                Join waitlist
              </Link>
            </div>
          ) : token ? (
            <>
              <p className="queue-status-eyebrow">{restaurantName}</p>
              <div className={`queue-status-badge queue-status-badge--${token.status}`}>
                {statusLabel(token.status)}
              </div>

              <div className="queue-status-ticket">
                <span className="queue-status-ticket__label">Your token</span>
                <div className="queue-status-ticket__number">{token.tokenLabel}</div>
              </div>

              <p className="queue-status-message">{statusMessage(token.status, token.position)}</p>

              <div className="queue-status-meta-row">
                {token.estimatedWaitMinutes != null && token.status === 'WAITING' && (
                  <span className="queue-status-pill">
                    <i className="fa fa-clock-o" aria-hidden="true" />
                    ~{token.estimatedWaitMinutes} min wait
                  </span>
                )}
                {token.partySize > 0 && (
                  <span className="queue-status-pill">
                    <i className="fa fa-users" aria-hidden="true" />
                    Party of {token.partySize}
                  </span>
                )}
                {ACTIVE_STATUSES.includes(token.status) && (
                  <span className="queue-status-pill queue-status-pill--live">
                    <i className="fa fa-circle" aria-hidden="true" />
                    Live updates
                  </span>
                )}
              </div>

              {ACTIVE_STATUSES.includes(token.status) && (
                <div className="queue-status-cancel">
                  <label htmlFor="cancelPhone">Phone number to confirm cancellation</label>
                  <div className="queue-status-cancel__row">
                    <input
                      id="cancelPhone"
                      type="tel"
                      className="queue-status-input"
                      placeholder="10-digit mobile number"
                      value={cancelPhone}
                      onChange={(e) => setCancelPhone(e.target.value)}
                      disabled={phoneFromLogin}
                      readOnly={phoneFromLogin}
                    />
                    <button
                      type="button"
                      className="queue-status-btn queue-status-btn--ghost"
                      onClick={() => setShowCancelConfirm(true)}
                      disabled={cancelling}
                    >
                      Leave waitlist
                    </button>
                  </div>
                  {phoneFromLogin && (
                    <p className="queue-status-field-hint">Phone number from your login account.</p>
                  )}
                </div>
              )}

              {errorMessage && <p className="queue-status-error">{errorMessage}</p>}

              <Link to={`/restaurants/${appName}/menu`} className="queue-status-link">
                Browse menu while you wait
              </Link>
            </>
          ) : null}
        </div>
      </PageContainer>
      <Footer />

      <ConfirmationModal
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={handleCancel}
        title="Leave waitlist?"
        message="Your token will be cancelled and you will lose your place in line."
        confirmText={cancelling ? 'Cancelling…' : 'Yes, leave waitlist'}
        cancelText="Keep my spot"
        confirmButtonClass="danger"
      />
    </div>
  );
};

export default QueueStatus;
